import React, { useState, useRef, useEffect } from 'react';
import { useSwipe } from '../context/SwipeContext';
import { MapPin, ChevronLeft, ChevronRight, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import MatchModal from '../components/MatchModal';
import { getPhotoUrl } from '../utils/photoUtils';
import { useGeolocation } from '../hooks/useGeolocation';

const SwipePage = () => {
  const { profiles, swipeRight, swipeLeft } = useSwipe();
  const { location, error: locationError, permission } = useGeolocation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [matchedProfile, setMatchedProfile] = useState(null);
  const [showMatchModal, setShowMatchModal] = useState(false);
  
  // Swipe gesture state
  const [dragStart, setDragStart] = useState(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const cardRef = useRef(null);

  const currentProfile = profiles[currentIndex];
  
  // Show location permission warning
  useEffect(() => {
    if (permission === 'denied' && !localStorage.getItem('location_warning_shown')) {
      toast({
        title: "Location Required",
        description: "Please enable location to see distance to other users",
        variant: "destructive",
        duration: 5000
      });
      localStorage.setItem('location_warning_shown', 'true');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permission]);

  const handleSwipe = async (direction) => {
    if (!currentProfile) return;

    setSwipeDirection(direction);

    setTimeout(async () => {
      if (direction === 'left') {
        await swipeLeft(currentProfile);
      } else {
        const result = await swipeRight(currentProfile);
        if (result.matched) {
          setMatchedProfile(result.profile);
          setShowMatchModal(true);
        }
      }

      setSwipeDirection(null);
      setPhotoIndex(0);
      setCurrentIndex(prev => prev + 1);
    }, 300);
  };

  // Touch/Mouse handlers for swipe gestures
  const handleDragStart = (e) => {
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    setDragStart(clientX);
    setIsDragging(true);
  };

  const handleDragMove = (e) => {
    if (!isDragging || dragStart === null) return;
    
    const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    const offset = clientX - dragStart;
    setDragOffset(offset);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    
    const threshold = 100; // pixels to swipe before triggering action
    
    if (Math.abs(dragOffset) > threshold) {
      // Swipe completed
      if (dragOffset > 0) {
        handleSwipe('right'); // Swiped right = like
      } else {
        handleSwipe('left'); // Swiped left = pass
      }
    }
    
    // Reset drag state
    setIsDragging(false);
    setDragStart(null);
    setDragOffset(0);
  };


  const nextPhoto = (e) => {
    e.stopPropagation();
    if (currentProfile && photoIndex < currentProfile.photos.length - 1) {
      setPhotoIndex(prev => prev + 1);
    }
  };

  const prevPhoto = (e) => {
    e.stopPropagation();
    if (photoIndex > 0) {
      setPhotoIndex(prev => prev - 1);
    }
  };

  if (!currentProfile) {
    return (
      <div className="flex items-center justify-center p-4 min-h-full">
        <div className="text-center">
          <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">No more profiles</h2>
          <p className="text-gray-600">Check back later for new matches!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center p-4 min-h-full bg-gray-50">
      <div className="w-full max-w-md">
        <div 
          ref={cardRef}
          className={`relative bg-white rounded-2xl shadow-xl overflow-hidden transition-all duration-300 ${
            swipeDirection === 'left' ? '-translate-x-full opacity-0' : 
            swipeDirection === 'right' ? 'translate-x-full opacity-0' : 
            ''
          }`}
          style={{ 
            aspectRatio: '3/4',
            transform: isDragging ? `translateX(${dragOffset}px) rotate(${dragOffset * 0.05}deg)` : '',
            cursor: isDragging ? 'grabbing' : 'grab',
            touchAction: 'none'
          }}
          onMouseDown={handleDragStart}
          onMouseMove={handleDragMove}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
        >
          {/* Photo */}
          <div className="relative w-full h-full">
            <img
              src={getPhotoUrl(currentProfile.photos, photoIndex)}
              alt={currentProfile.name}
              className="w-full h-full object-cover"
            />

            {/* Photo indicators */}
            {currentProfile.photos.length > 1 && (
              <div className="absolute top-4 left-4 right-4 flex gap-2">
                {currentProfile.photos.map((photo, idx) => (
                  <div
                    key={`${currentProfile.id}-photo-${idx}`}
                    className={`h-1 flex-1 rounded-full transition-colors ${
                      idx === photoIndex ? 'bg-white' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            )}

            {/* Navigation areas for photos */}
            {currentProfile.photos.length > 1 && (
              <>
                <div 
                  className="absolute left-0 top-0 bottom-0 w-1/2 cursor-pointer"
                  onClick={prevPhoto}
                />
                <div 
                  className="absolute right-0 top-0 bottom-0 w-1/2 cursor-pointer"
                  onClick={nextPhoto}
                />
              </>
            )}

            {/* Swipe direction indicators */}
            {isDragging && (
              <>
                {dragOffset > 30 && (
                  <div className="absolute top-8 right-8 bg-green-500 text-white px-6 py-3 rounded-xl font-bold text-2xl transform rotate-12 shadow-lg">
                    LIKE
                  </div>
                )}
                {dragOffset < -30 && (
                  <div className="absolute top-8 left-8 bg-red-500 text-white px-6 py-3 rounded-xl font-bold text-2xl transform -rotate-12 shadow-lg">
                    NOPE
                  </div>
                )}
              </>
            )}


            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

            {/* Profile info */}
            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
              <h2 className="text-3xl font-bold mb-1">
                {currentProfile.username}, {currentProfile.age}
              </h2>
              <div className="flex items-center gap-1 text-sm mb-2">
                <MapPin className="w-4 h-4" />
                <span>
                  {currentProfile.distance_km !== null && currentProfile.distance_km !== undefined
                    ? `${Math.round(currentProfile.distance_km)} km away`
                    : 'Distance unavailable'}
                </span>
              </div>
              {currentProfile.bio && (
                <p className="text-sm opacity-90 italic">
                  "{currentProfile.bio.length > 35 
                    ? `${currentProfile.bio.substring(0, 35)}...` 
                    : currentProfile.bio}"
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="text-center mt-8 text-sm text-gray-500">
          {profiles.length - currentIndex - 1} profiles remaining
        </div>
        <div className="text-center mt-2 text-xs text-gray-400">
          Swipe right to like, swipe left to pass
        </div>
      </div>

      {showMatchModal && matchedProfile && (
        <MatchModal
          profile={matchedProfile}
          onClose={() => setShowMatchModal(false)}
        />
      )}
    </div>
  );
};

export default SwipePage;
